import { Button, Drawer, Field, HStack, Icon, Input, Portal, Spinner, Switch, Text, VStack } from "@chakra-ui/react";
import { IoCloseSharp } from "react-icons/io5";
import { HiCheck, HiX } from "react-icons/hi";
import { useEffect, useState } from "react";
import UserService from "@/services/UserService";
import { showToast } from "@/components/ui/toaster";

const EditUserDrawer = ({ isOpen, onClose, onSaved, user }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || "");
            setLastName(user.lastName || "");
            setIsActive(user.isActive ?? true);
        }
    }, [user]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // This panel manages CyberJob system admins only.
            await UserService.update(user.id, { firstName, lastName, role: "Admin", isActive });
            showToast("User updated", null, "success");
            onSaved();
            onClose();
        } catch {
            showToast("Error", "Failed to update user", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="md" placement="end">
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner padding="4">
                    <Drawer.Content bg="bg.drawer" borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent="space-between" w="full">
                                <Text color="white">Edit User</Text>
                                <Icon fontSize={32} color="text.grey" onClick={onClose} cursor="pointer">
                                    <IoCloseSharp />
                                </Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack gap={4} alignItems="flex-start" w="full">
                                <Field.Root>
                                    <Field.Label color="white" fontSize={18}>Email</Field.Label>
                                    <Input variant="primary" value={user?.email || ""} readOnly opacity={0.6} />
                                </Field.Root>
                                <Field.Root required>
                                    <Field.Label color="white" fontSize={18}>
                                        First Name <Field.RequiredIndicator />
                                    </Field.Label>
                                    <Input variant="primary" placeholder="Enter first name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                </Field.Root>
                                <Field.Root required>
                                    <Field.Label color="white" fontSize={18}>
                                        Last Name <Field.RequiredIndicator />
                                    </Field.Label>
                                    <Input variant="primary" placeholder="Enter last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                                </Field.Root>
                                <Field.Root>
                                    <Field.Label color="white" fontSize={18}>Active</Field.Label>
                                    <Switch.Root checked={isActive} onCheckedChange={(e) => setIsActive(e.checked)} colorPalette="green">
                                        <Switch.HiddenInput />
                                        <Switch.Control>
                                            <Switch.Thumb>
                                                <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                                                    <HiCheck />
                                                </Switch.ThumbIndicator>
                                            </Switch.Thumb>
                                        </Switch.Control>
                                        <Switch.Label color="text.grey" fontSize={13}>{isActive ? "Active" : "Inactive"}</Switch.Label>
                                    </Switch.Root>
                                </Field.Root>
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent="flex-end" w="full" gap={3}>
                                <Button variant="outlined" textDecoration="unset" onClick={onClose} disabled={isSaving}>Cancel</Button>
                                <Button variant="primary" textDecoration="unset" onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? <Spinner size="sm" /> : "Save"}
                                </Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};

export default EditUserDrawer;
